// "Bố cục" (US1, SA B2): the host owns a versioned LayoutState and broadcasts it;
// guests apply it in version order; late joiners receive the latest on join.
//
// Transport: room.ts has no generic message API, so the state rides along the
// host-authoritative config message (`cfg`) under LAYOUT_KEY — the host already
// re-sends its config to every partner that joins, which covers late joiners.
// If the room ever exposes `sendLayout()` / a 'layout' event, those are used too
// (receivers de-duplicate by version).
//
// Also: a thin typed adapter over the compositor's co-presence API
// (setLayout / onInteraction / setCoaching / perf). Until the new compositor
// lands those calls no-op, so the booth still runs on the old Stage.

import type { BoothConfig } from '../net/protocol';

import type { Contact, InteractionState, LayoutSlot, LayoutState, Template } from '../booth/solver';

export type { Contact, InteractionState, LayoutSlot, LayoutState };
export type LayoutTemplate = Template;

export interface StagePerf { fps: number; mlMs: number; composeMs: number }

export const TEMPLATES: { id: LayoutTemplate; name: string; prompt: string }[] = [
  { id: 'side', name: 'Đứng cạnh', prompt: 'Cười thật tươi nào' },
  { id: 'heads', name: 'Chụm đầu', prompt: 'Nghiêng đầu vào nhau nào 💕' },
  { id: 'shoulder', name: 'Quàng vai', prompt: 'Quàng vai nhau nào' },
];

// ------------------------------------------------------------------ stage adapter

export interface StageExtras {
  /** true when the compositor implements the co-presence API */
  readonly supported: boolean;
  setLayout(s: LayoutState): void;
  onInteraction(cb: (s: InteractionState) => void): () => void;
  setCoaching(on: boolean): void;
  perf(): StagePerf | null;
}

export function stageExtras(stage: unknown): StageExtras {
  const s = stage as Partial<{
    setLayout(s: LayoutState): void;
    onInteraction(cb: (s: InteractionState) => void): () => void;
    setCoaching(on: boolean): void;
    perf(): StagePerf;
  }>;
  const fn = (k: keyof typeof s) => typeof s?.[k] === 'function';
  return {
    supported: fn('onInteraction'),
    setLayout: (l) => { if (fn('setLayout')) s.setLayout!(l); },
    onInteraction: (cb) => (fn('onInteraction') ? s.onInteraction!(cb) : () => {}),
    setCoaching: (on) => { if (fn('setCoaching')) s.setCoaching!(on); },
    perf: () => { try { return fn('perf') ? s.perf!() : null; } catch { return null; } },
  };
}

// ------------------------------------------------------------------ pure layout ops

/** Even spread like booth/layout.ts slotX: 1 → 0.5, 2 → 0.3 / 0.7, … */
export function spreadX(i: number, n: number) {
  if (n <= 1) return 0.5;
  const spread = Math.min(0.8, 0.4 * (n - 1));
  return 0.5 - spread / 2 + (spread * i) / (n - 1);
}

/** Version starts at the wall clock so a host that reloads still counts upwards. */
export function defaultLayout(hostId: string, version = Date.now()): LayoutState {
  return { version, template: 'side', mirror: true, slots: [{ peerId: hostId, x: 0.5, front: 0 }], mirrorPhoto: true };
}

const next = (s: LayoutState, patch: Partial<LayoutState>): LayoutState => ({ ...s, ...patch, version: s.version + 1 });

/**
 * Keep existing order, drop people who left, append newcomers on the right, re-spread.
 * Names travel with the slots so every device can phrase hints ("Vòng tay sang vai Lan").
 */
export function withMembers(s: LayoutState, members: { id: string; name: string }[]): LayoutState {
  const ids = members.map((m) => m.id);
  const nameOf = (id: string) => members.find((m) => m.id === id)?.name;
  const kept = [...s.slots].sort((a, b) => a.x - b.x).filter((sl) => ids.includes(sl.peerId));
  const added: LayoutSlot[] = ids.filter((id) => !kept.some((k) => k.peerId === id)).map((peerId) => ({ peerId, x: 0, front: 0 }));
  const all = [...kept, ...added].map((sl, i, arr) => ({ ...sl, x: spreadX(i, arr.length), name: nameOf(sl.peerId) || sl.name }));
  const same = all.length === s.slots.length && all.every((sl) => {
    const o = s.slots.find((x) => x.peerId === sl.peerId);
    return o && Math.abs(o.x - sl.x) < 1e-9 && o.front === sl.front && o.name === sl.name;
  });
  const giver = s.giver && ids.includes(s.giver) ? s.giver : undefined;
  return same && giver === s.giver ? s : next(s, { slots: all, giver });
}

/** Left-to-right order of peer ids. */
export const orderOf = (s: LayoutState) => [...s.slots].sort((a, b) => a.x - b.x).map((sl) => sl.peerId);

export function swapSlots(s: LayoutState, a: string, b: string): LayoutState {
  const A = s.slots.find((x) => x.peerId === a), B = s.slots.find((x) => x.peerId === b);
  if (!A || !B || a === b) return s;
  return next(s, { slots: s.slots.map((sl) => (sl.peerId === a ? { ...sl, x: B.x } : sl.peerId === b ? { ...sl, x: A.x } : sl)) });
}

/** null = automatic (body-driven); otherwise this person is forced in front. */
export function setFront(s: LayoutState, id: string | null): LayoutState {
  return next(s, { slots: s.slots.map((sl) => ({ ...sl, front: sl.peerId === id ? 1 : 0 })) });
}

export const frontOf = (s: LayoutState) => s.slots.find((sl) => sl.front > 0)?.peerId ?? null;

/** 'shoulder': who puts the arm over (null = picked automatically). */
export const setGiver = (s: LayoutState, id: string | null) => ((s.giver ?? null) === id ? s : next(s, { giver: id ?? undefined }));

export const setTemplate = (s: LayoutState, template: LayoutTemplate) => (s.template === template ? s : next(s, { template }));

/** v0.7: real camera scale ('natural', default) or faces matched to one size (v0.5). */
export const setScale = (s: LayoutState, scale: 'natural' | 'matched') => ((s.scale ?? 'natural') === scale ? s : next(s, { scale }));
export const setMirrorPhoto = (s: LayoutState, mirrorPhoto: boolean) => (s.mirrorPhoto === mirrorPhoto ? s : next(s, { mirrorPhoto }));

export function isLayoutState(v: unknown): v is LayoutState {
  const s = v as LayoutState;
  return !!s && typeof s.version === 'number' && ['side', 'heads', 'shoulder'].includes(s.template) &&
    Array.isArray(s.slots) && s.slots.every((sl) => sl && typeof sl.peerId === 'string' && typeof sl.x === 'number');
}

// ------------------------------------------------------------------ pose lock (US5)

export interface Readiness {
  ready: boolean;
  /** "Huy: đưa tay cao hơn" for everybody not ready */
  missing: string[];
}

/**
 * 'side' is always ready. Without a solver (old compositor) we cannot judge, so
 * nobody is blocked. Otherwise every person in the layout must be ready.
 */
export function readiness(layout: LayoutState, it: InteractionState | null, supported: boolean,
  nameOf: (id: string) => string): Readiness {
  if (layout.template === 'side' || !supported) return { ready: true, missing: [] };
  if (!it) return { ready: false, missing: ['Đang nhận diện tư thế…'] };
  const missing = orderOf(layout).filter((id) => it.ready[id] !== true)
    .map((id) => `${nameOf(id)}: ${it.hints[id] || it.depthHints[id] || 'chưa vào tư thế'}`);
  return { ready: missing.length === 0, missing };
}

/** Contacts present now but not in `prev` (→ haptic + tick). */
export function newContacts(prev: Contact[], now: Contact[]): Contact[] {
  const key = (c: Contact) => `${c.kind}:${[c.a, c.b].sort().join('|')}`;
  const had = new Set(prev.map(key));
  return now.filter((c) => !had.has(key(c)));
}

// ------------------------------------------------------------------ sync over the room

export const LAYOUT_KEY = 'layoutState';

interface RoomLike {
  sendConfig(c: BoothConfig): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(k: string, fn: (v: any) => void): unknown;
}

/** Split an incoming config into the plain BoothConfig and the piggy-backed layout. */
export function splitConfig(c: BoothConfig): { config: BoothConfig; layout: LayoutState | null } {
  const { [LAYOUT_KEY]: layout, ...rest } = c as BoothConfig & { [LAYOUT_KEY]?: unknown };
  return { config: rest as BoothConfig, layout: isLayoutState(layout) ? layout : null };
}

export function withLayout(c: BoothConfig, layout: LayoutState): BoothConfig {
  return { ...c, [LAYOUT_KEY]: layout } as BoothConfig;
}

export class LayoutSync {
  state: LayoutState;
  private listeners = new Set<(s: LayoutState) => void>();

  constructor(private room: RoomLike, private isHost: boolean, selfId: string) {
    // a guest accepts whatever the host has, however old its clock-based version is
    this.state = defaultLayout(selfId, isHost ? Date.now() : -1);
    // future dedicated channel, if the room grows one
    room.on('layout', (v: unknown) => { if (isLayoutState(v)) this.receive(v); });
    if (!isHost) room.on('partner', (p: unknown) => { if (!p) this.reset(); });
  }

  onChange(fn: (s: LayoutState) => void) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Guest: apply only newer versions. */
  receive(s: LayoutState) {
    if (this.isHost || s.version <= this.state.version) return false;
    this.state = s;
    this.listeners.forEach((f) => f(s));
    return true;
  }

  /** Guest: the host left — accept whatever version the next host starts with. */
  reset() {
    this.state = { ...this.state, version: -1 };
  }

  /** Host: change + broadcast (config carries the layout). */
  set(s: LayoutState, config: BoothConfig) {
    if (!this.isHost || s === this.state) return;
    this.state = s;
    this.listeners.forEach((f) => f(s));
    this.broadcast(config);
  }

  broadcast(config: BoothConfig) {
    if (!this.isHost) return;
    this.room.sendConfig(withLayout(config, this.state));
    const r = this.room as unknown as { sendLayout?: (s: LayoutState) => void };
    if (typeof r.sendLayout === 'function') r.sendLayout(this.state);
  }
}
